/**
 * ConductScoreMetricCard Component
 *
 * Metric card displaying the Conduct Score as a solid colored circle.
 * Uses the shared ConductScoreCircle component for consistent styling
 * across records and the Today page.
 *
 * Atomic level: Molecule
 * Composes: ConductScoreCircle, DeltaIndicator, Card (shadcn)
 * Used by: MetricStrip
 */

import React from 'react';
import { Card } from '../../../ui/card';
import { ConductScoreCircle } from '../../../ui/conduct-score-circle';
import { DeltaIndicator } from './DeltaIndicator';
import type { TrendDirection } from '../types/today.types';

export interface ConductScoreMetricCardProps {
  value: number; // 1-10 scale
  delta: number;
  trend: TrendDirection;
  noData?: boolean;
}

/**
 * Conduct Score metric card with solid colored circle.
 *
 * @example
 * ```tsx
 * <ConductScoreMetricCard
 *   value={7.2}
 *   delta={0.4}
 *   trend="up"
 * />
 * ```
 */
export function ConductScoreMetricCard({
  value,
  delta,
  trend: _trend,
  noData = false,
}: ConductScoreMetricCardProps): React.ReactElement {
  return (
    <Card className="gap-0 overflow-hidden rounded-2xl border-border/70 bg-card/90 p-0 shadow-[var(--shadow-card)]" padding="sm">
      <div className="space-y-4 p-5">
        <h3 className="font-jakarta text-[15px] font-bold tracking-[-0.02em] text-foreground">
          Conduct Score
        </h3>

        <div className="flex justify-center">
          {noData ? (
            <div className="flex h-28 w-28 items-center justify-center rounded-full border border-border/70 bg-muted/40">
              <span className="text-2xl font-semibold text-muted-foreground">N/A</span>
            </div>
          ) : (
            <ConductScoreCircle score={value} size="xl" showDecimal />
          )}
        </div>

        <div className="flex justify-center">
          <DeltaIndicator delta={delta} showValue={true} size="md" />
        </div>
      </div>
    </Card>
  );
}
